import { disc, hash, paintMound, px, type GroundLook } from './art';

/**
 * The Moon, painted in code: a black sky with Earth in it, grey highlands, crater rims and an old
 * relay dish, rocks and a flag passing in front. Cool blue haze (GDD: mood per planet). Every
 * scrolling layer tiles horizontally.
 */

type Ctx = CanvasRenderingContext2D;

export const MOON_COLORS = {
  sky: '#05040e',
  skyLow: '#0b0a1e',
  star: '#fff6d8',
  starDim: '#6f76a8',
  earthSea: '#2a5aa0',
  earthLand: '#3f8a4a',
  earthCloud: '#e8f0ff',
  earthShade: '#0e1a33',
  earthRim: '#7fb8ff',
  haze: '#3a4670',

  highland: '#25253a',
  highlandLit: '#3c3c56',
  rim: '#33334a',
  rimLit: '#585874',
  rimShade: '#1c1c2c',
  dish: '#2a2a3e',
  dishLit: '#6a6a88',

  fg: '#07070d',
  fgLit: '#171726',
  flag: '#1e1e30',
} as const;

const C = MOON_COLORS;

export const MOON_GROUND: GroundLook = {
  cap: 'dust',
  plank: 'metal',
  colors: {
    top: '#b4b4c4',
    topLit: '#e0e0ec',
    topDark: '#8a8a9c',
    body: '#77778a',
    bodyDark: '#56566a',
    speck: '#9a9aae',
    ink: '#121020',
    plank: '#6a7084',
    plankLit: '#aab2c4',
    plankDark: '#3a4052',
    hang: null,
  },
};

/** Black sky, a dense starfield, and Earth: seas, land, cloud, the night side. */
export function paintMoonSky(ctx: Ctx, width: number, height: number): void {
  px(ctx, C.sky, 0, 0, width, height);
  for (let y = Math.floor(height * 0.55); y < height; y += 2) {
    for (let x = (y / 2) % 2; x < width; x += 2) px(ctx, C.skyLow, x, y);
  }
  for (let i = 0; i < 160; i++) {
    const x = Math.floor(hash(i, 1, 31) * width);
    const y = Math.floor(hash(i, 2, 31) * height * 0.7);
    px(ctx, hash(i, 3, 31) < 0.6 ? C.starDim : C.star, x, y);
  }
  // Earth, where we came from: placed so it shows on the narrowest (480 px) screen.
  const ex = Math.min(width - 70, 380);
  const ey = 50;
  const r = 20;
  disc(ctx, ex, ey, r + 1, C.earthRim);
  for (let y = -r; y <= r; y++) {
    for (let x = -r; x <= r; x++) {
      if (x * x + y * y > r * r) continue;
      const land = hash(Math.floor((x + r) / 4), Math.floor((y + r) / 3), 41) > 0.6;
      const cloud = hash(Math.floor((x + r) / 3), Math.floor((y + r) / 2), 43) > 0.8;
      let color: string = cloud ? C.earthCloud : land ? C.earthLand : C.earthSea;
      const lit = (-x - y * 0.4) / r;
      if (lit < -0.35 || (lit < -0.15 && (x + y) % 2 === 0)) color = C.earthShade;
      px(ctx, color, ex + x, ey + y);
    }
  }
}

/** Grey highlands in two ridges, with crater notches cut in their skyline. */
export function paintMoonHighlands(ctx: Ctx, width: number, height: number): void {
  const ridge = (x: number, base: number, amp: number, phase: number) => {
    const t = (x / width) * Math.PI * 2;
    let h = base - amp * (0.6 * Math.sin(t + phase) + 0.25 * Math.sin(t * 3 + phase * 2));
    // Crater notches: shallow bowls in the skyline, at fixed spots so the texture tiles.
    for (let k = 0; k < 4; k++) {
      const cx = ((k + hash(k, 1, phase * 10)) / 4) * width;
      const d = Math.min(Math.abs(x - cx), width - Math.abs(x - cx));
      if (d < 18) h += Math.round(6 * Math.cos((d / 18) * (Math.PI / 2)));
    }
    return Math.round(h);
  };
  for (let x = 0; x < width; x++) {
    const top = ridge(x, 158, 30, 0.9);
    px(ctx, C.highland, x, top, 1, height - top);
    if (ridge(x - 1, 158, 30, 0.9) > top) px(ctx, C.highlandLit, x, top, 1, 2);
  }
  for (let x = 0; x < width; x++) {
    const top = ridge(x, 196, 16, 2.4);
    px(ctx, C.rim, x, top, 1, height - top);
    if (ridge(x - 1, 196, 16, 2.4) > top) px(ctx, C.rimLit, x, top, 1, 2);
  }
}

/** Crater rims and boulders nearer in, and an old relay dish on a mast. */
export function paintMoonRims(ctx: Ctx, width: number, height: number): void {
  const base = height - 52;
  for (let x = 0; x < width; x++) {
    const t = (x / width) * Math.PI * 2;
    const top = Math.round(base - 10 * Math.max(0, Math.sin(t * 2 + 0.4)) - 4 * Math.sin(t * 5));
    px(ctx, C.rimShade, x, top, 1, height - top);
    px(ctx, C.rim, x, top, 1, 2);
  }
  // Boulders on the rims, lit from the upper left.
  for (let i = 0; i < 7; i++) {
    const bx = Math.floor(hash(i, 6, 33) * width);
    const r = 4 + Math.floor(hash(i, 7, 33) * 7);
    for (const off of [-width, 0, width]) {
      disc(ctx, bx + off, base + 2, r, C.rimShade);
      disc(ctx, bx + off - 1, base + 1, r - 2, C.rim);
      px(ctx, C.rimLit, bx + off - Math.floor(r / 2), base - r + 3, 2, 1);
    }
  }
  // The relay dish: a lattice mast, a bowl, a feed horn.
  const mx = 250;
  for (let y = base - 46; y < base; y++) {
    px(ctx, C.dish, mx, y, 3, 1);
    if (y % 6 === 0) px(ctx, C.dishLit, mx - 2, y, 7, 1);
  }
  // The bowl: the lower half of a ring, opening upward, its inner edge lit.
  const by = base - 54;
  for (let y = -2; y <= 11; y++) {
    for (let x = -11; x <= 11; x++) {
      const d = Math.hypot(x, y);
      if (d <= 11 && d >= 8) px(ctx, d < 9 ? C.dishLit : C.dish, mx + 1 + x, by + y);
    }
  }
  // The feed horn on its strut.
  px(ctx, C.dishLit, mx + 1, by - 12, 1, 10);
  px(ctx, C.dishLit, mx, by - 13, 3, 2);
}

/** Dark rocks rising in front, a pebble scatter, and a planted flag (someone came before). */
export function paintMoonForeground(ctx: Ctx, width: number, height: number): void {
  const rock = (cx: number, r: number) => paintMound(ctx, cx, r, 1.4, height, C.fg, C.fgLit);
  rock(Math.round(width * 0.12), 22);
  rock(Math.round(width * 0.47), 34);
  rock(Math.round(width * 0.5), 18);
  rock(Math.round(width * 0.86), 14);
  // The flag: a pole and a stiff cloth held out by its top bar (no air to make it hang).
  const fx = Math.round(width * 0.72);
  px(ctx, C.fg, fx, height - 130, 2, 130);
  px(ctx, C.fgLit, fx, height - 130, 1, 60);
  px(ctx, C.fg, fx + 2, height - 128, 30, 1);
  px(ctx, C.flag, fx + 2, height - 127, 28, 18);
  for (let y = 0; y < 18; y += 4) px(ctx, C.fg, fx + 2, height - 127 + y, 28, 1);
}
